import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "@genie-ui/react";
import { Constants } from "@constants/index";
import config from "@config/index";
import PropTypes from "prop-types";
import { useMediaQuery } from "@mui/material";
import logout from "@utils/js/Logout";
import * as FullStory from "@fullstory/browser";
import { ACTIS_REPORTING } from "@utils/js/actis";
import "./header.scss";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchBar from "@components/atoms/Search/SearchBar";
import { getRedirectUrl } from "@utils/js/helper";
import {
  Header as GravityHeader,
  LeftNavigationComponent as LeftNavigation,
} from "@gravity/tools";
import { getCurrentEnv } from "@config";
import { getPageNameByPath } from "@utils/js/pageHelper";
import { redirectToLogin } from "@utils/js/utilities";

const Header = ({ currentUser, error }) => {
  const navigate = useNavigate();
  const [displayMenuIconsMobile, setDisplayMenuIconsMobile] = useState(false);
  const [userDashboardAccess, setUserDashboardAccess] = useState(false);
  const [userCmaAccess, setUserCmaAccess] = useState(false);
  const [currentPageNum, setCurrentPageNum] = useState(0);
  const [mountNavigation, setMountNavigation] = useState(true);
  const [dashboardId, setDashboardId] = useState("");
  setDashboardId
  const [showMobileNav, setShowMobileNav] = useState(false);
  const isTabletDevice = useMediaQuery("(max-width:1023px)");
  const isMobileDevice = useMediaQuery("(max-width:767px)");
  const { pathname } = useLocation();
  const gcomRedirectionLinkUrl = getRedirectUrl();
  const isFreemiumUser =
    currentUser?.dataHubRoles?.length === 1 &&
    currentUser?.dataHubRoles[0] === Constants.LABELS.DATAHUB_DASHBOARD;
  const isAccessDeniedPage = pathname.includes(Constants.ACCESS_DENIED);
  const isError = error || isAccessDeniedPage;

  useEffect(() => {
    const isGcomAccountSignIn = !!currentUser?.email
      ?.toLowerCase()
      ?.includes(Constants.GARTNER_DOMAIN);

    FullStory.identify(currentUser?.userId, {
      orgId_str: currentUser?.orgId,
      isGartnerAccount_str: `${isGcomAccountSignIn}`,
    });
  }, [currentUser]);

  useEffect(() => {
    const handleDashboardAccess = (isDashboardAccess) => {
      setUserDashboardAccess(isDashboardAccess);
    };
    window.loginFromHtmlEmitter.addEventListener(
      handleDashboardAccess,
      "DASHBOARD_ACCESS"
    );
    return () => {
      window.loginFromHtmlEmitter.removeEventListener(
        handleDashboardAccess,
        "DASHBOARD_ACCESS"
      );
    };
  }, []);

  useEffect(() => {
    const handleCmaAccess = (isCmaAccess) => {
      setUserCmaAccess(isCmaAccess);
    };
    window.loginFromHtmlEmitter.addEventListener(
      handleCmaAccess,
      "CMA_ACCESS"
    );
    return () => {
      window.loginFromHtmlEmitter.removeEventListener(
        handleCmaAccess,
        "CMA_ACCESS"
      );
    };
  }, []);

  useEffect(() => {
    const handlePageShow = (event) => {
      const loggedOut = localStorage.getItem(config.LOGOUT_EVENT_KEY) === "true";
      const onAccessDeniedPage = window.location.pathname.includes(Constants.ACCESS_DENIED);
      if (loggedOut && !onAccessDeniedPage) {
        redirectToLogin();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);


  useEffect(() => {
    const handleDashboardPageNumChange = (pageNum) => {
      setCurrentPageNum(pageNum);
    };
    window.loginFromHtmlEmitter.addEventListener(
      handleDashboardPageNumChange,
      "DASHBOARD_PAGE_NUM"
    );
    return () => {
      window.loginFromHtmlEmitter.removeEventListener(
        handleDashboardPageNumChange,
        "DASHBOARD_PAGE_NUM"
      );
    };
  }, []);

  useEffect(() => {
    const handleDashboardId = (dashboardId) => {
      setDashboardId(dashboardId);
    };
    window.loginFromHtmlEmitter.addEventListener(
      handleDashboardId,
      "DASHBOARD_ID"
    );
    return () => {
      window.loginFromHtmlEmitter.removeEventListener(
        handleDashboardId,
        "DASHBOARD_ID"
      );
    };
  }, []);

  useEffect(() => {
    FullStory.setVars("page", {
      pageName: pathname,
    });
  }, [pathname]);

  const {
    ACTIS: { EVENT_DATA },
    ROUTES: { LANDINGPAGE, BASE_URL },
    HEADER: { SEARCH_LABEL },
  } = Constants;

  const handleLogout = () => {
    ACTIS_REPORTING.USER_ACTION.push(EVENT_DATA.LOGOUT);
    window?.loginFromHtmlEmitter?.dispatchEvent(Constants.INTERCOM.LOGOUT);
    if (logout) {
      logout();
    }
  };

  const toggleDisplayMenuIconsMobile = () => {
    setDisplayMenuIconsMobile(!displayMenuIconsMobile);
  };

  const userMenuListOptions = [
    {
      id: "sign-out",
      label: Constants.HEADER.SIGN_OUT,
      onClick: handleLogout,
    },
  ];

  const handleAppLogoClick = () => {
    ACTIS_REPORTING.LINK.push(EVENT_DATA.PRIMARY_LOGO);
    window.open(gcomRedirectionLinkUrl, "_blank");
  };

  const isInvalidRoute = () => {
    const relativePath = pathname?.replace(/^\/data-hub/, "");
    if (relativePath === "/" || relativePath === "") return false;
    return !Constants.VALID_ROUTES?.some((route) => relativePath.startsWith(route));
  };

  const handleSecondaryLogoClickLogic = () => {
    ACTIS_REPORTING.LINK.push(EVENT_DATA.SECONDARY_LOGO);
    if (isError || isInvalidRoute()) {
      window.location.href = BASE_URL;
    } else {
      navigate(LANDINGPAGE);
    }
    setMountNavigation((prevState) => !prevState)
  };

  const onHelpCTAClick = () => {
    ACTIS_REPORTING.USER_ACTION.push({
      ...EVENT_DATA.INTERCOM_HELP,
    });
    window.open(config.INTERCOM_HELP_ROUTE, "_blank");
  };

  const onItemClick = (e, item) => {
    switch (item) {
      case "logo":
        handleAppLogoClick();
        break;
      case "appTitle":
        handleSecondaryLogoClickLogic();
        break;
      case "help":
        onHelpCTAClick();
        break;
      default:
        break;
    }
  };

  const handleAppLauncherExpand = () => {
    ACTIS_REPORTING.APP_LAUNCHER.push({
      label: Constants.ACTIS.APP_LAUNCHER.LABELS.APP_LAUNCHER_EXPANDED.replace(
        Constants.ACTIS.APP_LAUNCHER.COMMON_PLACEHOLDERS.PAGE_NAME,
        getPageNameByPath()
      ),
      action: Constants.ACTIS.APP_LAUNCHER.ACTIONS.APP_LAUNCHER_EXPANDED,
    });
  };

  const handleAppLauncherCollapse = () => {
    ACTIS_REPORTING.APP_LAUNCHER.push({
      label: Constants.ACTIS.APP_LAUNCHER.LABELS.APP_LAUNCHER_COLLAPSED.replace(
        Constants.ACTIS.APP_LAUNCHER.COMMON_PLACEHOLDERS.PAGE_NAME,
        getPageNameByPath()
      ),
      action: Constants.ACTIS.APP_LAUNCHER.ACTIONS.APP_LAUNCHER_COLLAPSED,
    });
  };

  const handleAppLauncherToolClicked = (...args) => {
    ACTIS_REPORTING.APP_LAUNCHER.push({
      label:
        Constants.ACTIS.APP_LAUNCHER.LABELS.APP_LAUNCHER_TOOL_CLICKED.replace(
          Constants.ACTIS.APP_LAUNCHER.COMMON_PLACEHOLDERS.PAGE_NAME,
          getPageNameByPath()
        ).replace(
          Constants.ACTIS.APP_LAUNCHER.COMMON_PLACEHOLDERS.TOOL_NAME,
          args[1]
        ),
      action: Constants.ACTIS.APP_LAUNCHER.ACTIONS.APP_LAUNCHER_TOOL_CLICKED,
    });
  };

  const handleAppLauncherSeeAllTools = () => {
    ACTIS_REPORTING.APP_LAUNCHER.push({
      label:
        Constants.ACTIS.APP_LAUNCHER.LABELS.APP_LAUNCHER_SEE_ALL_TOOLS.replace(
          Constants.ACTIS.APP_LAUNCHER.COMMON_PLACEHOLDERS.PAGE_NAME,
          getPageNameByPath()
        ),
      action: Constants.ACTIS.APP_LAUNCHER.ACTIONS.APP_LAUNCHER_SEE_ALL_TOOLS,
    });
  };

  const verticalNavRoutesMapper = {
    [Constants.VERTICAL_NAV_LABELS.Home]: ["/ppt-viewer"],
    [Constants.VERTICAL_NAV_LABELS.Dashboard]: ["/dashboard"],
    [Constants.VERTICAL_NAV_LABELS.Explore]: ["/report-list-view"],
    [Constants.VERTICAL_NAV_LABELS.Library]: ["/my-library"],
    [Constants.VERTICAL_NAV_LABELS.Playground]: ["/cma"],
  };

  const onNavigate = (links) => () => {
    let targetUrl = null;

    if (links === Constants.VERTICAL_NAV_LABELS.Home) {
      targetUrl = "/";
    } else if (links === Constants.VERTICAL_NAV_LABELS.Dashboard) {
      targetUrl = "/dashboard";
    } else {
      [targetUrl] = verticalNavRoutesMapper[links];
    }

    const relativePath = location.pathname?.replace(/^\/data-hub/, "");
    const isSameRoute = relativePath === targetUrl;

    if (isSameRoute) {
      if (targetUrl === "/dashboard") {
        if (currentPageNum === 0 && !dashboardId) return;
      } else return;
    }

    if (targetUrl === "/dashboard") {
      window.loginFromHtmlEmitter.dispatchEvent("NAVIGATE_TO_DASHBOARD");
    }

    if (isInvalidRoute()) {
      window.location.href = BASE_URL + targetUrl;
    } else {
      navigate(targetUrl);
    }
  };

  const getActiveNavLabel = () => {
    const relativePath = pathname?.replace(/^\/data-hub/, "");
    if (relativePath === "/" || relativePath === "") {
      return Constants.VERTICAL_NAV_LABELS.Home;
    }

    for (const [label, paths] of Object.entries(verticalNavRoutesMapper)) {
      const isPathIncluded = paths.some((path) => relativePath.includes(path));
      if (isPathIncluded) {
        return label;
      }
    }
    return "";
  };

  const currentActiveLabel = getActiveNavLabel();

  const verticalNavLinks = () => {
    const allOptions = [
      {
        icon: "HOME",
        id: "home",
        label: Constants.VERTICAL_NAV_LABELS.Home,
        type: "INTERNAL_LINK",
        onClick: onNavigate(`${Constants.VERTICAL_NAV_LABELS.Home}`),
        isHighlighted:
          currentActiveLabel === Constants.VERTICAL_NAV_LABELS.Home,
      },
      {
        icon: "LIST",
        id: "explore",
        label: Constants.VERTICAL_NAV_LABELS.Explore,
        type: "INTERNAL_LINK",
        onClick: onNavigate(`${Constants.VERTICAL_NAV_LABELS.Explore}`),
        isHighlighted:
          currentActiveLabel === Constants.VERTICAL_NAV_LABELS.Explore,
      },
      {
        icon: "SAVE",
        id: "save",
        label: Constants.VERTICAL_NAV_LABELS.Library,
        type: "INTERNAL_LINK",
        onClick: onNavigate(`${Constants.VERTICAL_NAV_LABELS.Library}`),
        isHighlighted:
          currentActiveLabel === Constants.VERTICAL_NAV_LABELS.Library,
      },
    ];
    const datahubOptions = {
      icon: "OVERVIEW_DASHBOARD",
      id: "dashboard",
      label: Constants.VERTICAL_NAV_LABELS.Dashboard,
      type: "INTERNAL_LINK",
      onClick: onNavigate(`${Constants.VERTICAL_NAV_LABELS.Dashboard}`),
      isHighlighted:
        currentActiveLabel === Constants.VERTICAL_NAV_LABELS.Dashboard,
    };

    const cmaOptions = {
      icon: "ANALYTICS",
      id: "cma",
      label: Constants.VERTICAL_NAV_LABELS.Playground,
      type: "INTERNAL_LINK",
      onClick: onNavigate(`${Constants.VERTICAL_NAV_LABELS.Playground}`),
      isHighlighted:
        currentActiveLabel === Constants.VERTICAL_NAV_LABELS.Playground,
    }

    if (userDashboardAccess) {
      allOptions.splice(1, 0, datahubOptions);
    }

    if (userCmaAccess) {
      allOptions.splice(allOptions?.length, 0, cmaOptions);
    }

    return allOptions;
  };

  return (
    <div className="App">
      <GravityHeader
        className="navbar"
        appName={Constants.HEADER.SECONDARY_LOGO_LABEL}
        logoUrl={`${config.S3_ASSET_PATH}/header/logo.svg`}
        userFirstName={currentUser?.firstName || ""}
        userLastName={currentUser?.lastName || ""}
        userEmail={currentUser?.email || ""}
        showAppLauncher={true}
        showHelp={true}
        userMenuListOptions={userMenuListOptions}
        onItemClick={onItemClick}
        isMenuOpen={showMobileNav}
        onMenuClick={(open) => setShowMobileNav(open)}
        appLauncherProps={{
          environment: getCurrentEnv(),
          onAppLauncherClick: handleAppLauncherExpand,
          onAppLauncherClose: handleAppLauncherCollapse,
          onToolClick: handleAppLauncherToolClicked,
          onSeeAllToolsClick: handleAppLauncherSeeAllTools,
        }}
        {...(!isFreemiumUser &&
          !isError &&
          !isAccessDeniedPage && {
            secondaryComponent: (
              <>
                {isTabletDevice &&
                  (displayMenuIconsMobile ? (
                    <div className="search-flex-mobile search-tray-mobile">
                      <ArrowBackIcon
                        className="back-arrow"
                        onClick={toggleDisplayMenuIconsMobile}
                      />
                      <SearchBar />
                    </div>
                  ) : (
                    <div className="search-tray-mobile">
                      <IconButton
                        type="button"
                        aria-label="search"
                        onClick={toggleDisplayMenuIconsMobile}
                      >
                        <img
                          src={`${config.S3_ASSET_PATH}/header/SearchIcon.svg`}
                        />
                      </IconButton>
                      <Typography
                        variant="h7"
                        className="navbar__mylibrary-text"
                      >
                        {SEARCH_LABEL}
                      </Typography>
                    </div>
                  ))}
                {!isTabletDevice && (
                  <div className="search-tray-desktop">
                    <SearchBar />
                  </div>
                )}
              </>
            ),
          })}
      />
      {!isError && !isAccessDeniedPage && (
        <LeftNavigation
          key={mountNavigation}
          isMobilePrimaryNavOpen={isMobileDevice && showMobileNav}
          onToggleMobilePrimaryNav={(isOpen) => setShowMobileNav(isOpen)}
          primaryItems={verticalNavLinks()}
        />
      )}
    </div>
  );
};

Header.propTypes = {
  currentUser: PropTypes.object.isRequired,
  error: PropTypes.bool,
};

export default Header;
